import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
} from '@angular/core'
import { NgForOf, NgIf } from '@angular/common'
import { LogoAssets, logoAssetsToken } from 'shared-components'
import { Card, trickNull, GamePlayer, gameNull, Game } from 'game-services'
import { playerScoreCardNull } from 'game-play-services'
import { GameScoresComponent } from '../game-scores/game-scores.component'

@Component({
  selector: 'sans-game-info-section',
  standalone: true,
  imports: [NgForOf, NgIf, GameScoresComponent],
  templateUrl: './game-info-section.component.html',
  styleUrl: './game-info-section.component.scss',
})
export class GameInfoSectionComponent implements OnChanges {
  @Input() public game: Game = gameNull
  @Input() public talonCards: Card[] = []
  @Input() public userPlayerScoreCard = playerScoreCardNull
  @Input() public leftOpponentPlayerScoreCard = playerScoreCardNull
  @Input() public rightOpponentPlayerScoreCard = playerScoreCardNull

  @Output() userCardSelected = new EventEmitter<Card>()
  @Output() talonCardSelected = new EventEmitter<Card>()

  public userUsername = ''
  public userBidLabel = ''
  public shouldShowUserDefendOption = false
  public userDeclaredDefendOption = ''
  public shouldShowUserPreviousTrick = false
  public userPreviousTrick = trickNull

  public leftOpponentUsername = ''
  public leftOpponentBidLabel = ''
  public shouldShowLeftOpponentDefendOption = false
  public leftOpponentDeclaredDefendOption = ''
  public shouldShowLeftOpponentPreviousTrick = false
  public leftOpponentPreviousTrick = trickNull
  public leftOpponentDoublingCountRoundsPlayed = 0

  public rightOpponentUsername = ''
  public rightOpponentBidLabel = ''
  public shouldShowRightOpponentDefendOption = false
  public rightOpponentDeclaredDefendOption = ''
  public shouldShowRightOpponentPreviousTrick = false
  public rightOpponentPreviousTrick = trickNull
  public rightOpponentDoublingCountRoundsPlayed = 0

  public shouldShowContractSection = false
  public isDoublingCountRound = false
  public declarerUsername = ''
  public isContractDeclared = false
  public contractName = ''
  public shouldShowTalonCardsInGameInfo = false
  public shouldShowDeclaredDefendOptions = false
  public shouldShowPreviousTrick = false
  public shouldShowBiddingSection = false
  public doublingCountRoundsLeftToQueue = 0
  public doublingCountRoundsQueued = 0

  public declarerLabel = $localize`:Dashboard component|Declarer label:Declarer`
  public contractLabel = $localize`:Dashboard component|Contract label:Contract`
  public talonLabel = $localize`:Dashboard component|Talon label:Talon`
  public doublingCountRoundsLeftLabel = $localize`:Dashboard component|Doubling count rounds label:Doubling count rounds left to queue`
  public doublingCountRoundsQueuedLabel = $localize`:Dashboard component|Doubling count rounds label:Doubling count rounds queued`

  public previousTrickLabel = $localize`:Dashboard component|Previous trick label:Previous trick played`
  public doublingCountRoundLabel = $localize`:Dashboard component|Doubling count round label indicator:Doubling count round`
  public gameStatusLabel = ''

  private passBidLabel = $localize`:Dashboard component|Pass bid label:Pass bid`

  public constructor(@Inject(logoAssetsToken) public logoAssets: LogoAssets) {}

  public ngOnChanges(): void {
    this.userUsername = this.game.user.username
    this.userBidLabel = this.getPlayerBidLabel(this.game.user)
    this.shouldShowUserDefendOption = this.game.user.hasDeclaredDefendOption
    this.userDeclaredDefendOption =
      this.game.user.defendOption.getDefendOptionName()
    this.shouldShowUserPreviousTrick = this.game.user.isPlayingGame
    this.userPreviousTrick = this.game.user.previousTrick

    this.leftOpponentUsername = this.game.leftOpponent.username
    this.leftOpponentBidLabel = this.getPlayerBidLabel(this.game.leftOpponent)
    this.shouldShowLeftOpponentDefendOption =
      this.game.leftOpponent.hasDeclaredDefendOption
    this.leftOpponentDeclaredDefendOption =
      this.game.leftOpponent.defendOption.getDefendOptionName()
    this.shouldShowLeftOpponentPreviousTrick =
      this.game.leftOpponent.isPlayingGame
    this.leftOpponentPreviousTrick = this.game.leftOpponent.previousTrick
    this.leftOpponentDoublingCountRoundsPlayed =
      this.game.leftOpponent.doublingCountRoundsPlayed

    this.rightOpponentUsername = this.game.rightOpponent.username
    this.rightOpponentBidLabel = this.getPlayerBidLabel(this.game.rightOpponent)
    this.shouldShowRightOpponentDefendOption =
      this.game.rightOpponent.hasDeclaredDefendOption
    this.rightOpponentDeclaredDefendOption =
      this.game.rightOpponent.defendOption.getDefendOptionName()
    this.shouldShowRightOpponentPreviousTrick =
      this.game.rightOpponent.isPlayingGame
    this.rightOpponentPreviousTrick = this.game.rightOpponent.previousTrick
    this.rightOpponentDoublingCountRoundsPlayed =
      this.game.rightOpponent.doublingCountRoundsPlayed

    this.shouldShowContractSection = this.game.shouldShowContractSection
    this.isDoublingCountRound = this.game.isDoublingCountRound
    this.declarerUsername = this.game.getDeclarerUsername()
    this.isContractDeclared = this.game.isContractDeclared
    this.contractName = this.game.contract.contractName
    this.shouldShowTalonCardsInGameInfo =
      this.game.shouldShowTalonCardsInGameInfo
    this.shouldShowDeclaredDefendOptions =
      this.game.shouldShowDeclaredDefendOptions
    this.shouldShowPreviousTrick = this.game.shouldShowPreviousTrick
    this.shouldShowBiddingSection = this.game.shouldShowBiddingSection
    this.doublingCountRoundsLeftToQueue =
      this.game.doublingCountRoundsLeftToQueue
    this.doublingCountRoundsQueued = this.game.doublingCountRoundsQueued
    this.gameStatusLabel = this.getGameStatusLabel()
  }

  public cardTrackBy(_: number, card: Card): string {
    return `${card.rank}${card.suite}`
  }

  private getPlayerBidLabel(player: GamePlayer): string {
    return player.hasPlacedBid
      ? player.bid.getBidName()
      : player.hasPassedBid
        ? this.passBidLabel
        : ''
  }

  private getGameStatusLabel(): string {
    if (this.game.gameStatus === 'DealingCards') {
      return $localize`:Dashboard component|Game status label:Dealing cards`
    }

    if (this.game.gameStatus === 'Bidding') {
      return $localize`:Dashboard component|Game status label:Bidding`
    }

    if (this.game.gameStatus === 'ContractDeclaration') {
      return $localize`:Dashboard component|Game status label:Contract declaration`
    }

    if (this.game.gameStatus === 'DefenderDeclaration') {
      return $localize`:Dashboard component|Game status label:Defender declaration`
    }

    if (this.game.gameStatus === 'Playing') {
      return $localize`:Dashboard component|Game status label:Playing`
    }

    if (this.game.gameStatus === 'Settling') {
      return $localize`:Dashboard component|Game status label:Settling`
    }

    return $localize`:Dashboard component|Game status label:Completed`
  }
}
